import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Flag } from "lucide-react";

/**
 * Frontend for POST /api/content-reports (iter60 legal compliance).
 * Distinct from ReportUserDialog which targets a *user* profile:
 * this one targets a specific piece of *content* (report, listing, message).
 *
 * Props:
 *  - targetType: "report" | "marketplace_listing" | "message" | "marketplace_message"
 *  - targetId:   the identifier of the target document
 *  - label:      button label override (falls back to i18n `report_content_btn`)
 *  - size, variant, className, testid: passthrough to the trigger button
 */
const REASONS = [
  "harassment", "hate", "sexual", "spam", "privacy",
  "illegal", "impersonation", "abuse", "other",
];

export function ReportContentButton({
  targetType, targetId, label,
  size = "sm", variant = "outline", className = "", testid,
}) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  return (
    <>
      <Button
        data-testid={testid || `content-report-btn-${targetId}`}
        variant={variant}
        size={size}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`rounded-full text-xs hover:bg-red-50 hover:text-red-800 ${className}`}
      >
        <Flag size={12} className="mr-1" /> {label || t("report_content_btn")}
      </Button>
      {open && (
        <ReportContentDialog
          open={open}
          onOpenChange={setOpen}
          targetType={targetType}
          targetId={targetId}
        />
      )}
    </>
  );
}

export function ReportContentDialog({ open, onOpenChange, targetType, targetId }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason || !targetId || !targetType) return;
    setBusy(true);
    try {
      await api.post("/content-reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        details: details || null,
      });
      toast.success(t("report_content_sent"));
      onOpenChange(false);
      setDetails("");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="content-report-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag size={16} className="text-red-600" /> {t("report_content_title")}
          </DialogTitle>
          <DialogDescription>{t("report_content_desc")}</DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
              {t("report_user_reason") || "Motivo"}
            </div>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="cr-reason" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`cr-reason-${r}`}>
                    {t(`report_reason_${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
              {t("report_content_details")}
            </div>
            <Textarea
              data-testid="cr-details"
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 2000))}
              className="rounded-xl text-sm"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full hover:bg-slate-100">
            {t("cancel")}
          </Button>
          <Button
            data-testid="cr-submit"
            onClick={submit}
            disabled={busy || !reason}
            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
          >
            {t("report_content_send")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReportContentButton;
